import { Dispatch } from 'react';
import { AxiosRequestConfig } from 'axios';
import { API_KEYS } from '../../interfaces/api';
import {
  apiRequestLoading,
  apiRequestFailure,
  apiRequestReset,
  apiRequestSuccess,
  apiRequestResetAll,
  apiRequestUpdateResponse,
} from '../slices/requestSlice';
import { show as showNotification } from '../slices/notification';
import { makeRequest } from '../../api/requests';
import { AppState, Datum } from '../../interfaces';
import { history } from '../store';
import { hideModal } from '../slices/modal';

export const resetApiThunk = (key: API_KEYS) => apiRequestReset({ key });

export const updateResponse = (key: API_KEYS, response: Datum) =>
  apiRequestUpdateResponse({ key, response });

export const resetAllApiThunk = () => apiRequestResetAll();

export const logOut = () => (dispatch: Dispatch<any>) => {
  dispatch(resetApiThunk(API_KEYS.LOGIN));
  dispatch(resetApiThunk(API_KEYS.GET_WALLET_BALANCE));
  dispatch(resetApiThunk(API_KEYS.GET_SHIPMENT_DETAILS));
  dispatch(resetApiThunk(API_KEYS.GET_DELIVERY_MANIFEST));

  dispatch(resetApiThunk(API_KEYS.WALLET_TRANSACTIONS_SHIPMENT_HISTORY));
  dispatch(hideModal());
  window.localStorage.removeItem('quick-shipping');
  window.localStorage.removeItem('overseas-shipping');
};

export interface ThunkConfig<Req> {
  key: API_KEYS;
  request: Req;
  apiOptions: Omit<AxiosRequestConfig, 'baseurl' | 'url' | 'method'>;
  urlParams?: Datum;
  showSuccessAlert?: boolean;
  successAlertMessage?: string;
  showErrorAlert?: boolean;
  errorAlertMessage?: string;
  dispatchOnSuccess?: (dispatch: Dispatch<any>, req: Req, res: any) => void;
  dispatchOnError?: (dispatch: Dispatch<any>, error: any) => void;
  onSuccessCallback?: (req: Req, res: any) => void;
  onErrorCallback?: (error: any) => void;
}

export interface AlphaConfig<Req> {
  key: API_KEYS;
  request: Req;
  apiOptions: Omit<AxiosRequestConfig, 'baseurl' | 'url' | 'method'>;
  urlParams?: Datum;
  showSuccessAlert?: boolean;
  successAlertMessage?: string;
  showErrorAlert?: boolean;
  errorAlertMessage?: string;
  dispatchOnSuccess?: (dispatch: Dispatch<any>, req: Req, res: any) => void;
  dispatchOnError?: (dispatch: Dispatch<any>, error: any) => void;
  onSuccessCallback?: (req: Req, res: any) => void;
  onErrorCallback?: (error: any) => void;
}

export const apiThunk =
  <Req>({
    key,
    request,
    apiOptions,
    urlParams,
    showSuccessAlert = true,
    successAlertMessage,
    showErrorAlert = true,
    errorAlertMessage,
    dispatchOnSuccess,
    dispatchOnError,
    onSuccessCallback,
    onErrorCallback,
  }: ThunkConfig<Req>) =>
  async (dispatch: Dispatch<any>, getState?: () => AppState) => {
    const authParams = getState && getState().apiRequest[API_KEYS.LOGIN];
    const token = authParams?.response?.Object?.access_token;

    dispatch(apiRequestLoading({ key, request: request as unknown as Datum }));
    try {
      const response = await makeRequest(token, key, apiOptions, urlParams);
      if ([200, 201, 204].includes(response.status)) {
        dispatch(apiRequestSuccess({ key, response: response.data as any }));
        if (showSuccessAlert) {
          dispatch(
            showNotification({
              alertType: 'success',
              message: successAlertMessage || 'Operation successful',
            })
          );
        }
        if (dispatchOnSuccess) {
          dispatchOnSuccess(dispatch, request, response);
        }
        if (onSuccessCallback) {
          onSuccessCallback(request, response);
        }
      }
    } catch (error: any) {
      if (error.status === 401 || error.status === 403) {
        dispatch(logOut());
        history.push('/sign-in');
      }
      dispatch(apiRequestFailure({ key, error: error.data }));
      if (showErrorAlert) {
        dispatch(
          showNotification({
            alertType: 'error',
            message:
              errorAlertMessage ||
              error?.data?.ShortDescription ||
              error?.data?.Message ||
              error?.data?.data ||
              error?.data?.message,
          })
        );
      }
      if (dispatchOnError) {
        dispatchOnError(dispatch, error);
      }
      if (onErrorCallback) {
        onErrorCallback(error);
      }
    }
  };

export const apiTickectmanThunk =
  <Req>({
    key,
    request,
    apiOptions,
    urlParams,
    showSuccessAlert = true,
    successAlertMessage,
    showErrorAlert = true,
    errorAlertMessage,
    dispatchOnSuccess,
    dispatchOnError,
    onSuccessCallback,
    onErrorCallback,
  }: ThunkConfig<Req>) =>
  async (dispatch: Dispatch<any>, getState?: () => AppState) => {
    const authTicketParams =
      getState && getState().apiRequest[API_KEYS.TICKETMAN_ACCESSTOKEN];
    const tickeToken = authTicketParams?.response?.access_token;

    dispatch(apiRequestLoading({ key, request: request as unknown as Datum }));
    try {
      const response = await makeRequest(
        tickeToken,
        key,
        apiOptions,
        urlParams
      );
      if ([200, 201, 204].includes(response.status)) {
        dispatch(apiRequestSuccess({ key, response: response.data as any }));
        if (showSuccessAlert) {
          dispatch(
            showNotification({
              alertType: 'success',
              message: successAlertMessage || 'Operation successful',
            })
          );
        }
        if (dispatchOnSuccess) {
          dispatchOnSuccess(dispatch, request, response);
        }
        if (onSuccessCallback) {
          onSuccessCallback(request, response);
        }
      }
    } catch (error: any) {
      if (error.status === 401 || error.status === 403) {
        alert(
          'Bills Payment is not available at the moment, Please try again later.'
        );
        // showNotification({
        //   alertType: 'error',
        //   message: 'Bills Payment is not available at the moment, Please try again later.',
        // })
      }
      dispatch(apiRequestFailure({ key, error: error.data }));
      if (showErrorAlert) {
        dispatch(
          showNotification({
            alertType: 'error',
            message:
              errorAlertMessage ||
              error?.data?.ShortDescription ||
              error?.data?.Message ||
              error?.data?.data ||
              error?.data?.message ||
              error?.error_description,
          })
        );
      }
      if (dispatchOnError) {
        dispatchOnError(dispatch, error);
      }
      if (onErrorCallback) {
        onErrorCallback(error);
      }
    }
  };

export const apiAlphaThunk =
  <Req>({
    key,
    request,
    apiOptions,
    urlParams,
    showSuccessAlert = true,
    successAlertMessage,
    showErrorAlert = true,
    errorAlertMessage,
    dispatchOnSuccess,
    dispatchOnError,
    onSuccessCallback,
    onErrorCallback,
  }: AlphaConfig<Req>) =>
  async (dispatch: Dispatch<any>, getState?: () => AppState) => {
    const authAlphaParams =
      getState && getState().apiRequest[API_KEYS.ALPHA_LOGIN];
    const alphaToken = authAlphaParams?.response?.results?.token;

    dispatch(apiRequestLoading({ key, request: request as unknown as Datum }));
    try {
      const response = await makeRequest(
        alphaToken,
        key,
        apiOptions,
        urlParams
      );
      if ([200, 201, 204].includes(response.status)) {
        dispatch(apiRequestSuccess({ key, response: response.data as any }));
        if (showSuccessAlert) {
          dispatch(
            showNotification({
              alertType: 'success',
              message: successAlertMessage || 'Operation successful',
            })
          );
        }
        if (dispatchOnSuccess) {
          dispatchOnSuccess(dispatch, request, response);
        }
        if (onSuccessCallback) {
          onSuccessCallback(request, response);
        }
      }
    } catch (error: any) {
      if (error.status === 401 || error.status === 403) {
        // alert('Bills Payment is not available at the moment, Please try again later.');
        showNotification({
          alertType: 'error',
          message: 'Alpha service not available at the moment',
        });
      }
      dispatch(apiRequestFailure({ key, error: error.data }));
      if (showErrorAlert) {
        dispatch(
          showNotification({
            alertType: 'error',
            message:
              errorAlertMessage ||
              error?.data?.ShortDescription ||
              error?.data?.Message ||
              error?.data?.data ||
              error?.data?.message ||
              error?.error_description,
          })
        );
      }
      if (dispatchOnError) {
        dispatchOnError(dispatch, error);
      }
      if (onErrorCallback) {
        onErrorCallback(error);
      }
    }
  };
